import * as React from "react"
import { Editor } from "@tiptap/react"
import { Button } from "@/components/tiptap-ui-primitive/button"
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/tiptap-ui-primitive/popover"
import { Separator } from "@/components/tiptap-ui-primitive/separator"
import { BanIcon } from "@/components/tiptap-icons/ban-icon"

export interface TextColorPopoverColor {
  label: string
  value: string
  border?: string
}

export interface TextColorPopoverContentProps {
  editor?: Editor | null
  colors?: TextColorPopoverColor[]
  onClose?: () => void
}

export interface TextColorPopoverProps {
  editor?: Editor | null
  colors?: TextColorPopoverColor[]
  hideWhenUnavailable?: boolean
}

export const DEFAULT_TEXT_COLORS: TextColorPopoverColor[] = [
  { label: "Black", value: "#000000" },
  { label: "Red", value: "#e53935" },
  { label: "Green", value: "#43a047" },
  { label: "Blue", value: "#1e88e5" },
  { label: "Orange", value: "#fb8c00" },
  { label: "Purple", value: "#8e24aa" },
  { label: "Gray", value: "#757575" },
]

export function TextColorPopoverContent({
  editor,
  colors = DEFAULT_TEXT_COLORS,
  onClose,
}: TextColorPopoverContentProps) {
  if (!editor) return null

  const setColor = (color: string) => {
    editor.chain().focus().setColor(color).run()
    onClose?.()
  }

  const removeColor = () => {
    editor.chain().focus().unsetColor().run()
    onClose?.()
  }

  return (
    <div className="tiptap-color-highlight-content">
      <div className="tiptap-button-group" data-orientation="horizontal">
        {colors.map((color) => (
          <Button
            key={color.value}
            type="button"
            style={{ color: color.value }}
            aria-label={`${color.label} text color`}
            onClick={() => setColor(color.value)}
            data-style="ghost"
          >
            <span
              style={{
                display: "inline-block",
                width: 20,
                height: 20,
                borderRadius: "50%",
                background: color.value,
                border: color.border ? `1px solid ${color.border}` : "1px solid #ccc",
              }}
            />
          </Button>
        ))}
      </div>
      <Separator />
      <div className="tiptap-button-group">
        <Button
          onClick={removeColor}
          aria-label="Remove text color"
          type="button"
          data-style="ghost"
        >
          <BanIcon className="tiptap-button-icon" />
        </Button>
      </div>
    </div>
  )
}

export function TextColorPopover({
  editor,
  colors = DEFAULT_TEXT_COLORS,
  hideWhenUnavailable = false,
}: TextColorPopoverProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  const isActive = editor?.isActive("textStyle") && !!editor?.getAttributes("textStyle").color

  if (!editor || !editor.isEditable) {
    return null
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          data-style="ghost"
          data-active-state={isActive ? "on" : "off"}
          aria-label="Text color"
        >
          <span
            style={{
              display: "inline-block",
              width: 18,
              height: 18,
              borderRadius: "50%",
              background: editor.getAttributes("textStyle").color || "#000",
              border: "1px solid #ccc",
              marginRight: 4,
            }}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent aria-label="Text colors">
        <TextColorPopoverContent
          editor={editor}
          colors={colors}
          onClose={() => setIsOpen(false)}
        />
      </PopoverContent>
    </Popover>
  )
} 